import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FC } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  AppBar,
  Box,
  Hidden,
  IconButton,
  Toolbar,
  SvgIcon,
  Typography,
  Chip,
} from '@mui/material';
import { Menu as MenuIcon, Gift } from 'react-feather';
import Logo from 'src/components/Logo';
import Account from './Account';
import Settings from './Settings';
import Contact from './Contact';
import UserContext from 'src/contexts/UserContext';
import HtmlTooltip from 'src/components/HtmlTooltip';
import { useDispatch, useSelector } from 'src/store';
import {
  fetchRewardsBalance,
  setActiveCompany,
  setRealtimeBalance,
} from 'src/slices/rewards';
import {
  dismissNotifications,
  getPendingNotifications,
} from 'src/models/Rewards';
import { useSnackbar } from 'notistack';
import { db } from 'src/lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';

interface TopBarProps {
  className?: string;
  onMobileNavOpen?: () => void;
  onDesktopNavOpen?: () => void;
}

const getRewardsAnimationSessionKey = (companyId: string) =>
  `rewards-initial-animation:${companyId}`;

const TopBar: FC<TopBarProps> = ({
  className,
  onMobileNavOpen,
  onDesktopNavOpen,
  ...rest
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const userContext = useContext(UserContext).userData;
  const rewardsBalance = useSelector((state) => state.rewards.balance);
  const rewardsLoading = useSelector((state) => state.rewards.loading);
  const { enqueueSnackbar } = useSnackbar();
  const isCheckingNotificationsRef = useRef(false);
  const [isRewardsAnimating, setIsRewardsAnimating] = useState(false);
  const rewardsAnimationTimeoutRef = useRef<number | null>(null);
  const hasAnimatedInitialRewardsRef = useRef(false);
  const previousRewardsBalanceRef = useRef<number | null>(null);

  const triggerRewardsAnimation = useCallback(() => {
    setIsRewardsAnimating(false);

    window.requestAnimationFrame(() => {
      setIsRewardsAnimating(true);

      if (rewardsAnimationTimeoutRef.current) {
        window.clearTimeout(rewardsAnimationTimeoutRef.current);
      }

      rewardsAnimationTimeoutRef.current = window.setTimeout(() => {
        setIsRewardsAnimating(false);
      }, 1400);
    });
  }, []);

  const checkPendingRewardNotifications = useCallback(
    async (companyId: string) => {
      if (isCheckingNotificationsRef.current) {
        return;
      }

      try {
        isCheckingNotificationsRef.current = true;
        const notifications = await getPendingNotifications(companyId);

        if (!notifications.length) {
          return;
        }

        for (const notification of notifications) {
          enqueueSnackbar(notification.message, {
            variant: 'success',
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center',
            },
          });

          await dismissNotifications(companyId, notification.category);
        }
      } catch (error) {
        console.error(
          'Failed to process pending rewards notifications:',
          error
        );
      } finally {
        isCheckingNotificationsRef.current = false;
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    if (!userContext?.companyID) {
      return undefined;
    }

    const companyId = userContext.companyID;
    hasAnimatedInitialRewardsRef.current =
      window.sessionStorage.getItem(getRewardsAnimationSessionKey(companyId)) ===
      'true';
    previousRewardsBalanceRef.current = null;

    dispatch(setActiveCompany(companyId));
    // Warm-start balance so chip renders immediately even if listener is delayed.
    dispatch(fetchRewardsBalance(companyId));
    checkPendingRewardNotifications(companyId);

    const unsubscribeBalance = onSnapshot(
      doc(db, 'companies', companyId),
      (snapshot) => {
        const nextBalance = snapshot.data()?.rewardsBalance;

        if (typeof nextBalance === 'number') {
          dispatch(setRealtimeBalance({ companyId, balance: nextBalance }));
        } else {
          dispatch(setRealtimeBalance({ companyId, balance: 0 }));
        }
      },
      () => {
        // Fallback to API when listener cannot stream updates.
        dispatch(fetchRewardsBalance(companyId));
      }
    );

    const handleFocus = () => {
      dispatch(fetchRewardsBalance(companyId));
      checkPendingRewardNotifications(companyId);
    };

    const handleRewardsBalanceRefresh = () => {
      dispatch(fetchRewardsBalance(companyId));
      checkPendingRewardNotifications(companyId);
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener(
      'rewardsBalanceRefresh',
      handleRewardsBalanceRefresh
    );

    return () => {
      unsubscribeBalance();
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener(
        'rewardsBalanceRefresh',
        handleRewardsBalanceRefresh
      );
    };
  }, [checkPendingRewardNotifications, dispatch, userContext?.companyID]);

  useEffect(() => {
    if (!userContext?.companyID || rewardsLoading || rewardsBalance === null) {
      return;
    }

    const previousBalance = previousRewardsBalanceRef.current;

    if (!hasAnimatedInitialRewardsRef.current) {
      hasAnimatedInitialRewardsRef.current = true;
      window.sessionStorage.setItem(
        getRewardsAnimationSessionKey(userContext.companyID),
        'true'
      );
      previousRewardsBalanceRef.current = rewardsBalance;
      triggerRewardsAnimation();
      return;
    }

    if (previousBalance !== rewardsBalance) {
      previousRewardsBalanceRef.current = rewardsBalance;
      triggerRewardsAnimation();
    }
  }, [
    rewardsBalance,
    rewardsLoading,
    triggerRewardsAnimation,
    userContext?.companyID,
  ]);

  useEffect(() => {
    return () => {
      if (rewardsAnimationTimeoutRef.current) {
        window.clearTimeout(rewardsAnimationTimeoutRef.current);
      }
    };
  }, []);

  const handleRewardsClick = () => {
    history.push('/app/rewards');
  };

  return (
    <>
      <AppBar
        enableColorOnDark
        className={className}
        {...rest}
        sx={{ backgroundColor: 'background.default', color: 'text.primary' }}
      >
        <Toolbar
          sx={{
            minHeight: '40px !important',
            height: 40,
          }}
        >
          <Hidden lgUp>
            <IconButton color="inherit" onClick={onMobileNavOpen} size="large">
              <SvgIcon fontSize="small">
                <MenuIcon />
              </SvgIcon>
            </IconButton>
          </Hidden>
          <Hidden lgDown>
            <IconButton color="inherit" onClick={onDesktopNavOpen} size="large">
              <SvgIcon fontSize="small">
                <MenuIcon />
              </SvgIcon>
            </IconButton>
            <Box sx={{ pr: 0.625, mt: 0.625 }}>
              <RouterLink to="/app/reports/dashboard">
                <Logo style={{ width: '24px', height: '24px' }} />
              </RouterLink>
            </Box>
            <Box>
              <RouterLink
                to="/app/reports/dashboard"
                style={{ textDecoration: 'none' }}
              >
                <Typography
                  color="textPrimary"
                  variant="h3"
                  sx={{ fontFamily: 'Times', textDecoration: 'none' }}
                >
                  <b>ReInvestWealth</b>
                </Typography>
              </RouterLink>
            </Box>
          </Hidden>
          <Box sx={{ ml: 2, flexGrow: 1 }} />
          <HtmlTooltip title="Rewards">
            <IconButton
              color="inherit"
              onClick={handleRewardsClick}
              size="large"
            >
              <Box
                sx={{
                  transformOrigin: 'center',
                  animation: isRewardsAnimating
                    ? 'rewardsShakePop 1s cubic-bezier(0.22, 1, 0.36, 1)'
                    : 'none',
                  '@keyframes rewardsShakePop': {
                    '0%': {
                      transform: 'translateX(0) scale(1)',
                      filter: 'drop-shadow(0 0 0 rgba(255, 152, 0, 0))',
                    },
                    '18%': {
                      transform: 'translateX(-4px) scale(1.12) rotate(-8deg)',
                      filter: 'drop-shadow(0 0 6px rgba(255, 152, 0, 0.5))',
                    },
                    '34%': {
                      transform: 'translateX(5px) scale(1.2) rotate(8deg)',
                      filter: 'drop-shadow(0 0 10px rgba(255, 152, 0, 0.7))',
                    },
                    '50%': {
                      transform: 'translateX(-3px) scale(1.14) rotate(-5deg)',
                    },
                    '68%': {
                      transform: 'translateX(3px) scale(1.1) rotate(4deg)',
                    },
                    '84%': {
                      transform: 'translateX(0) scale(1.16)',
                      filter: 'drop-shadow(0 0 12px rgba(255, 152, 0, 0.5))',
                    },
                    '100%': {
                      transform: 'translateX(0) scale(1)',
                      filter: 'drop-shadow(0 0 0 rgba(255, 152, 0, 0))',
                    },
                  },
                }}
              >
                <Chip
                  icon={
                    <SvgIcon fontSize="small">
                      <Gift />
                    </SvgIcon>
                  }
                  label="Rewards"
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    backgroundColor: 'secondary.main',
                    color: 'white',
                    transform: isRewardsAnimating ? 'scale(1.08)' : 'scale(1)',
                    boxShadow: isRewardsAnimating
                      ? '0 0 0 8px rgba(255, 152, 0, 0.2), 0 4px 14px rgba(255, 152, 0, 0.35)'
                      : 'none',
                    transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                    '& .MuiChip-label': {
                      px: 1.1,
                    },
                    '& .MuiChip-icon': {
                      color: 'white',
                      ml: 0.75,
                      mr: -0.5,
                      width: 15,
                      height: 15,
                    },
                  }}
                />
              </Box>
            </IconButton>
          </HtmlTooltip>

          <Settings />
          <Contact />
          <Box sx={{ ml: 2 }}>
            <Account />
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

TopBar.propTypes = {
  className: PropTypes.string,
  onMobileNavOpen: PropTypes.func,
  onDesktopNavOpen: PropTypes.func,
};

TopBar.defaultProps = {
  onMobileNavOpen: () => {},
  onDesktopNavOpen: () => {},
};

export default TopBar;
